/**
 * Analytics API client for portfolio visualization data
 *
 * This API client provides tenant-aware analytics endpoints that respect
 * user isolation. The main Analytics page currently uses Grafana embeds,
 * but these API methods are still valuable for:
 *
 * 1. The Recharts components (preserved but not currently used)
 *    See: webapp/frontend/src/components/charts/index.ts
 *
 * 2. Future use cases:
 *    - Admin dashboard
 *    - Mobile views
 *    - Email digest generation
 *    - API-first multi-tenant access (Phase 10H)
 *
 * Note: The getPrice() and some history methods may be used by other
 * pages (e.g., What-If Calculator). Don't delete without checking usages.
 */
import { apiClient } from './client';
import type {
  PortfolioHistory,
  AllocationBreakdown,
  PerformanceBreakdown,
  PortfolioSummary,
  AllocationGroupBy,
  PerformanceSortBy,
} from '../types/analytics';
import type { PriceLookup } from '../types/what-if';

const BASE_PATH = '/api/analytics';

export interface PortfolioHistoryParams {
  days?: number;
  portfolioId?: string;
  tagIds?: string[];
}

export interface AllocationParams {
  groupBy?: AllocationGroupBy;
  portfolioId?: string;
  tagIds?: string[];
}

export interface PerformanceParams {
  limit?: number;
  sortBy?: PerformanceSortBy;
  portfolioId?: string;
  tagIds?: string[];
}

export const analyticsApi = {
  /**
   * Get portfolio value history over time
   */
  getPortfolioHistory: async (params: PortfolioHistoryParams = {}): Promise<PortfolioHistory> => {
    const queryParams: Record<string, string> = {};

    if (params.days) queryParams.days = params.days.toString();
    if (params.portfolioId) queryParams.portfolio_id = params.portfolioId;
    if (params.tagIds?.length) queryParams.tag_ids = params.tagIds.join(',');

    const response = await apiClient.get<PortfolioHistory>(`${BASE_PATH}/portfolio-history`, {
      params: queryParams,
    });
    return response.data;
  },

  /**
   * Get portfolio allocation breakdown
   */
  getAllocation: async (params: AllocationParams = {}): Promise<AllocationBreakdown> => {
    const queryParams: Record<string, string> = {};

    if (params.groupBy) queryParams.group_by = params.groupBy;
    if (params.portfolioId) queryParams.portfolio_id = params.portfolioId;
    if (params.tagIds?.length) queryParams.tag_ids = params.tagIds.join(',');

    const response = await apiClient.get<AllocationBreakdown>(`${BASE_PATH}/allocation`, {
      params: queryParams,
    });
    return response.data;
  },

  /**
   * Get holdings performance rankings
   */
  getHoldingsPerformance: async (params: PerformanceParams = {}): Promise<PerformanceBreakdown> => {
    const queryParams: Record<string, string> = {};

    if (params.limit) queryParams.limit = params.limit.toString();
    if (params.sortBy) queryParams.sort_by = params.sortBy;
    if (params.portfolioId) queryParams.portfolio_id = params.portfolioId;
    if (params.tagIds?.length) queryParams.tag_ids = params.tagIds.join(',');

    const response = await apiClient.get<PerformanceBreakdown>(`${BASE_PATH}/holdings-performance`, {
      params: queryParams,
    });
    return response.data;
  },

  /**
   * Get current price for a symbol (from snapshot or live API)
   */
  getPrice: async (symbol: string): Promise<PriceLookup> => {
    const response = await apiClient.get<PriceLookup>(`${BASE_PATH}/price/${encodeURIComponent(symbol)}`);
    return response.data;
  },

  /**
   * Get portfolio summary with totals for dashboard cards
   */
  getSummary: async (portfolioId?: string, tagIds?: string[]): Promise<PortfolioSummary> => {
    const queryParams: Record<string, string> = {};

    if (portfolioId) queryParams.portfolio_id = portfolioId;
    if (tagIds?.length) queryParams.tag_ids = tagIds.join(',');

    const response = await apiClient.get<PortfolioSummary>(`${BASE_PATH}/summary`, {
      params: queryParams,
    });
    return response.data;
  },
};

export default analyticsApi;
